import React, { useState } from "react";

import { useUIDSeed } from "../../helpers/useUIDSeed";
import { ControlWrapper } from "../ControlWrapper";

import { RawSelect } from "./RawSelect";

export interface SelectOption<Values extends string | number = string> {
  /**
   * The display value for the option. Defaults to the `value` prop.
   */
  label?: string;
  /**
   * The value of the option. This acts like a reference for the option, and is what is compared for determining which option matches the `selectedOptionValue`in the Select props.
   */
  value: Values;
  /**
   * Disables the option. This sets styles and prevents the Selects onChange callback to fire.
   */
  disabled?: boolean;
  /**
   * Displays the provided element to the left of the option label.
   */
  labelDecorator?: React.ReactNode;
}

export interface SelectProps<Values extends string | number = string> {
  /**
   * Text label for when the input is disabled
   */
  disabledLabel?: string;
  /**
   * Error string to display. When truthy, sets error styling
   */
  error?: string;
  /**
   * Helper string to display beneath input
   */
  helperText?: string;
  /**
   * Helper string to display in the button next to helper text
   */
  helperButtonLabel?: string | null | false | React.ReactNode;
  /**
   * ID value used for the trigger button and menu. Auto generated if not provided
   */
  id?: string;
  /**
   * Label text
   */
  label?: React.ReactNode;
  /**
   * Function called when an option is selected and is passed the option object selected
   */
  onChange?: (option: SelectOption<Values>) => void;
  /**
   * Function called when helper button is clicked
   */
  helperButtonClick?: () => void;
  /**
   * Flag to set input as required (appends `*` to label)
   */
  required?: boolean;
  /**
   * Text that will be displayed next to the label when `required` is false.
   */
  optionalLabelText?: string;
  /**
   * Text to display if no option has been selected
   */
  defaultText?: string;
  /**
   * Tooltip to display
   */
  tooltip?: React.ReactNode;
  /**
   * Flag to set input to full width
   */
  fullWidth?: boolean;
  /**
   * Helper button position
   *
   * @default top
   */
  helperButtonPosition?: "top" | "bottom";
  /**
   * Test ID applied to the select box
   */
  ["data-testid"]?: string;
  /**
   * Prevents input changes and applies disabled styling
   */
  disabled?: boolean;
  /**
   * Array of options
   */
  options: SelectOption<Values>[];
  /**
   * The currently selected option `value`.
   */
  selectedOptionValue?: Values | null;
  /**
   * Applies compact styling
   */
  compact?: boolean;
  /**
   * Accessibility "labeledBy" prop: https://www.w3.org/TR/WCAG20-TECHS/ARIA7.html
   */
  ariaLabelledby?: string;
  /**
   * Accessibility "describedBy" prop: https://www.w3.org/TR/WCAG20-TECHS/ARIA1.html
   */
  ariaDescribedby?: string;
  /**
   * CSS class name applied to the UL on the list menu
   */
  menuClassName?: string;
  /**
   * CSS class name applied to the trigger button
   */
  triggerClassName?: string;
  /**
   * If true, the dropdown menu popover will be rendered in a portal. Portals
   * are useful for rendering components outside of their parent component's DOM
   * hierarchy, such as modals or tooltips. If false, the popover will be rendered
   * in place.
   * @default true
   */
  renderPopoverInPortal?: boolean;
}

/**
 * An accessible select component.
 *
 * - Follows accessibility guidelines for dropdown ([collapsible listbox](https://www.w3.org/TR/wai-aria-practices-1.1/examples/listbox/listbox-collapsible.html))
 */
export function Select<Values extends string | number = string>(
  props: SelectProps<Values>
) {
  const [isOpen, setOpen] = useState(false);
  const generateUID = useUIDSeed(props.id, props.label);

  const {
    disabled,
    disabledLabel,
    error,
    helperText,
    helperButtonLabel,
    helperButtonClick,
    id,
    "data-testid": dataTestId,
    label,
    required,
    optionalLabelText,
    defaultText,
    tooltip,
    fullWidth,
    helperButtonPosition = "bottom",
    renderPopoverInPortal = true,
    ...inputProps
  } = props;

  const selectId = id || generateUID("select-id");

  return (
    <ControlWrapper
      fullWidth={fullWidth}
      tooltip={tooltip}
      disabled={disabled}
      error={error}
      helperText={helperText}
      helperButtonLabel={undefinedIfFalse(helperButtonLabel)}
      helperButtonClick={helperButtonClick}
      helperButtonPosition={helperButtonPosition}
      inputId={selectId}
      label={label}
      required={required}
      optionalLabelText={optionalLabelText}
      outsideClick={
        isOpen
          ? // istanbul ignore next - simple closure for downstream function
            () => setOpen(false)
          : null
      }
      renderControl={({ labelId, helperTextId, errorId }) => (
        <RawSelect<Values>
          ariaLabelledby={labelId}
          ariaDescribedby={`${errorId} ${helperTextId}`}
          disabled={disabled}
          disabledLabel={disabledLabel}
          error={error}
          id={selectId}
          dataTestId={dataTestId}
          defaultText={defaultText}
          isOpen={isOpen}
          setOpen={setOpen}
          renderPopoverInPortal={renderPopoverInPortal}
          {...inputProps}
        />
      )}
    />
  );
}

function undefinedIfFalse(
  value: string | false | undefined | null | React.ReactNode
): React.ReactNode {
  // istanbul ignore next - simple type guard/mapper
  return value === false ? undefined : value;
}
